import {Api} from "../api/api";
import {LinkPager} from "../link-pager";
import {PlayVideo} from "../play-video";
import {initProductSlider} from "../productSlider";
import {renderElement} from "../utils/common";
import {ShowMoreTemplate} from "./consts";

/**
 * Функционал отвечающий за загрузку элементов по кнопке "Показать ещё"
 *
 * @class ShowMore
 */
class ShowMore {
  #catalogElement;
  #btnElement;
  #filterFormElement;
  #paginationElement;
  #listElement;
  #formUrl;
  #currentPage;
  #perPage;
  #pagesAmount;
  #isLoading;
  #templateType;
  #formData;
  #linkPager;
  #playVideo;
  #saleFilter;
  #sortFilter;

  #api = new Api();

  /**
   * Creates an instance of ShowMore.
   * @param {object} {
   *     btnSelector = ".js-show-more-btn", - селектор кнопки "показать ещё"
   *     formSelector = ".js-filter-inform", - селектор формы фильтрации
   *     paginationSelector = ".js-pagination", - селектор элемента пагинации
   *     listSelector = ".js-catalog-list", - селектор листинга элементов
   *   }
   * @memberof ShowMore
   */
  constructor() {
    this.#catalogElement = document.querySelector(".js-catalog");
    this.#btnElement = document.querySelector(".js-show-more-btn");
    this.#paginationElement = document.querySelector(".js-pagination");
    this.#filterFormElement = document.querySelector(".js-filter-inform");

    if (this.#catalogElement && this.#filterFormElement && this.#btnElement && this.#paginationElement) {
      this.#init();
    }
  }

  /**
   * Инициализация параметров, добавление слушателей
   *
   * @memberof ShowMore
   */
  #init() {
    this.#listElement = this.#catalogElement.querySelector(".js-catalog-list");
    this.#saleFilter = this.#catalogElement.querySelector(".js-filter-item[data-type='sale']");
    this.#sortFilter = this.#catalogElement.querySelector(".js-filter-item[data-type='sort']");

    this.#templateType = this.#btnElement.dataset.type;
    this.#formUrl = this.#filterFormElement.action;
    this.#linkPager = new LinkPager(this.#paginationElement);
    this.#playVideo = new PlayVideo({autoInit: false});

    this.#addListeners();
  }

  #bindEvents() {
    this.onBtnElementClick = this.onBtnElementClick.bind(this);
  }

  /**
   * Добавление слушателей на элементы
   *
   * @memberof ShowMore
   */
  #addListeners() {
    this.#bindEvents();

    this.#btnElement.addEventListener("click", this.onBtnElementClick);
  }

  /**
   * Установка статуса кнопке загрузки, добавление/удаление класса для показа прелоадера
   *
   * @param {boolean} isLoading - Отправляется ли запрос
   * @memberof ShowMore
   */
  #setBtnLoadingStatus(isLoading) {
    this.#btnElement.disabled = isLoading;

    isLoading ? this.#btnElement.classList.add("show-more--loading") : this.#btnElement.classList.remove("show-more--loading");
  }

  #getSortValue() {
    if (this.#sortFilter) {
      const sortRadioElements = Array.from(this.#sortFilter.querySelectorAll("input[type='radio']"));
      const sortInputElement = sortRadioElements.find((element) => element.checked);

      return !sortInputElement ? "" : sortInputElement.dataset.id;
    }
  }

  #getDiscountValue() {
    return this.#saleFilter && this.#saleFilter.classList.contains("filter-item--active") ? 1 : "";
  }

  /**
   * Рендер необходимых элементов после загрузки данных
   *
   * @param {Array} dataList - Массив с необходимыми данными для рендера элементов
   * @memberof ShowMore
   */
  #renderElements(dataList) {
    if (ShowMoreTemplate.hasOwnProperty(this.#templateType)) {
      const productAmount = this.#getProductAmount();

      dataList.forEach((dataElement, index) => {
        const renderedElement = renderElement(this.#listElement, ShowMoreTemplate[this.#templateType](dataElement, productAmount + (index + 1)));
        const sliderElement = renderedElement.querySelector(".js-item-slider");
        const videoElement = renderedElement.querySelector(".js-item-video");

        if (sliderElement) {
          this.#initSlider(sliderElement);
        }

        if (videoElement) {
          this.#initVideo(videoElement);
        }
      });
    }
  }

  /**
   * Получение количества элементов в списке
   *
   * @return {number}
   * @memberof ShowMore
   */
  #getProductAmount() {
    return this.#listElement.children.length;
  }

  /**
   * Инициализация слайдера внутри карточки продукта (перелистывание изображения по движению курсора)
   *
   * @param {HTMLElement} element -Элемент слайдера
   * @memberof ShowMore
   */
  #initSlider(element) {
    initProductSlider(element);
  }

  /**
   * Инициализация слушателей на элемент видео в карточке товара
   *
   * @param {HTMLVideoElement} element - Элемент видео
   * @memberof ShowMore
   */
  #initVideo(element) {
    this.#playVideo.addListeners(element);
  }

  /**
   * Обновление счётчика текущей страницы
   *
   * @memberof ShowMore
   */
  #updateCurrentPage() {
    this.#currentPage++;

    this.#btnElement.dataset.currentPage = this.#currentPage;
  }

  /**
   * Обновление разметки пагинации после выполнения ajax запроса по рендеру карточек
   *
   * @param {number} currentPage - Номер текущей страницы
   * @param {number} pagesAmount - Общее число страниц
   * @param {string} searchParams - Строка состоящая из параметров фильтрации
   * @param {number} perPage - Кол-во элементов необходимых для рендера для одной страницы
   * @memberof ShowMore
   */
  #updatePagination(currentPage, pagesAmount, searchParams, perPage) {
    this.#paginationElement.innerHTML = this.#linkPager.getUpdatedPaginationElement(currentPage, pagesAmount, searchParams, perPage);
  }

  /**
   * Получение строки с параметрами фильтрации
   *
   * @return {string}
   * @memberof ShowMore
   */
  #getSearchParams() {
    const searchParams = new URLSearchParams();

    for (let [key, value] of this.#formData) {
      searchParams.append(key, value);
    }

    searchParams.append("sort", this.#getSortValue());
    searchParams.append("isDiscount", this.#getDiscountValue());

    return searchParams;
  }

  /**
   * Отправка запроса и получение данных с сервера
   *
   * @memberof ShowMore
   */
  async #fetchMoreItems() {
    this.#isLoading = true;
    this.#setBtnLoadingStatus(this.#isLoading);
    const searchParams = this.#getSearchParams();

    this.#pagesAmount = this.#linkPager.getLastPage();

    const {code, answer} = await this.#api.customQuery(
      `${this.#formUrl}?${searchParams}&page=${this.#currentPage}&per-page=${this.#perPage}&ajax=1`,
      {},
    );

    if (code === 200) {
      this.#isLoading = false;
      this.#setBtnLoadingStatus(this.#isLoading);

      if (answer.data.length > 0) {
        this.#renderElements(answer.data);
        this.#updateCurrentPage();
        this.#updatePagination(this.#currentPage, this.#pagesAmount, `${this.#formUrl}?${searchParams}`, this.#perPage);
        window.history.pushState({}, "", `${location.pathname}?${searchParams}&page=${this.#currentPage}&per-page=${this.#perPage}`);

        if (this.#currentPage === this.#pagesAmount) {
          this.#btnElement.remove();
        }
      } else {
        this.#btnElement.remove();
      }
    } else {
      this.#isLoading = false;
      this.#setBtnLoadingStatus(this.#isLoading);
    }
  }

  /**
   * Определение переменных и отправка запроса на сервер при клике на кнопку "Показать ещё"
   *
   * @memberof ShowMore
   */
  onBtnElementClick() {
    this.#currentPage = this.#btnElement.dataset.currentPage;
    this.#perPage = this.#btnElement.dataset.perPage;
    this.#formData = new FormData(this.#filterFormElement);

    this.#fetchMoreItems();
  }
}

export {ShowMore};
